// REL-036-039 — deploy-mid-session stale-chunk recovery (caller side).
//
// lazyWithReload wraps React.lazy so a stale-bundle import failure (the
// previous build's chunk hashes no longer exist after a deploy) auto-reloads
// the page once instead of crashing into the ErrorBoundary. The failure shows
// up several ways: mobile Safari 'TypeError: Importing a module script failed',
// other browsers 'Failed to fetch dynamically imported module', and — for a CSS
// dependency that 404s after a deploy (e.g. the NON-precached mapbox-gl chunk
// behind the Sprays map tab) — Vite's 'Unable to preload CSS for …'. The
// decision + loop guard live in chunkReload.ts (unit-tested, side-effects
// injected); this module is the browser-side glue.
//
// Two entry points can fire for the SAME failed import:
//   • lazyWithReload's catch, when the rejection reaches the lazy boundary.
//   • the global 'vite:preloadError' listener (installPreloadErrorReload),
//     which Vite dispatches DURING preload — before the rejection propagates.
// recoverFromChunkError() funnels both through handleChunkLoadError exactly
// ONCE per page load: the first caller drives the verdict and it's latched, so
// a same-page-load second caller can't trip the loop-guard window twice in one
// tick. A real reload resets module state, so the cross-reload guard (which
// lives in sessionStorage) still works across page loads.

import { lazy, type ComponentType } from 'react'
import { handleChunkLoadError, browserChunkReloadDeps, type ChunkReloadDecision } from '@/lib/chunkReload'
import { autoRecoverStaleBundle } from '@/lib/swRecovery'

let decided: ChunkReloadDecision | null = null

function recoverFromChunkError(): ChunkReloadDecision {
  if (decided) return decided
  decided = handleChunkLoadError(browserChunkReloadDeps(autoRecoverStaleBundle))
  return decided
}

export function lazyWithReload<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await importer()
    } catch (err) {
      // 'rethrow' → let the ErrorBoundary show (offline, or the loop guard gave
      // up: the chunk really is gone). 'reloading' → hang on the Suspense
      // fallback while the reload chunkReload kicked off takes over.
      if (recoverFromChunkError() === 'rethrow') throw err
      return new Promise<{ default: T }>(() => {})
    }
  })
}

// Global net for Vite's preload failures across EVERY dynamic import — route
// boundaries AND in-page lazy boundaries (e.g. the Sprays map tab) — not just
// the ones wrapped in lazyWithReload. Fires during Vite's preload dispatch so
// the guarded reload starts as early as possible. Install once from main.tsx.
//
// We deliberately do NOT call event.preventDefault(): letting Vite rethrow
// preserves the wrapped boundary's clean behaviour (Suspense-hang while
// reloading, ErrorBoundary rethrow on give-up). recoverFromChunkError() dedupes
// the boundary's follow-up call so the loop guard still runs exactly once.
export function installPreloadErrorReload(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('vite:preloadError', () => {
    recoverFromChunkError()
  })
}
